<script setup lang="ts">
import { computed } from 'vue';
import type { Component } from 'vue';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@manta/ui-kit';
import { CheckIcon, CircleFadingPlus } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';

interface FacetOption {
  label: string;
  value: string;
  icon?: Component;
}

const props = defineProps<{
  title: string;
  options: FacetOption[];
  modelValue: string[];
  /** Optional per-value counts shown on the right of each row. */
  facets?: Record<string, number>;
}>();

const emit = defineEmits<{ 'update:modelValue': [string[]] }>();

const { t } = useI18n();

const selected = computed(() => new Set(props.modelValue));

function toggle(value: string) {
  const next = new Set(props.modelValue);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  emit('update:modelValue', Array.from(next));
}

function clear() {
  emit('update:modelValue', []);
}
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button variant="outline" size="sm" class="h-8 border-dashed">
        <CircleFadingPlus class="mr-2 h-4 w-4" />
        {{ title }}
        <template v-if="selected.size > 0">
          <Separator orientation="vertical" class="mx-2 h-4" />
          <Badge variant="secondary" class="rounded-sm px-1 font-normal">
            {{ selected.size }}
          </Badge>
        </template>
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-[220px] p-0" align="start">
      <Command
        :filter-function="
          (list: FacetOption[], term: string) =>
            list.filter((i) => i.label.toLowerCase().includes(term))
        "
      >
        <CommandInput :placeholder="title" />
        <CommandList>
          <CommandEmpty>{{ t('sessions.filters.empty') }}</CommandEmpty>
          <CommandGroup>
            <CommandItem
              v-for="option in options"
              :key="option.value"
              :value="option"
              @select="toggle(option.value)"
            >
              <div
                class="mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary"
                :class="
                  selected.has(option.value)
                    ? 'bg-primary text-primary-foreground'
                    : 'opacity-50 [&_svg]:invisible'
                "
              >
                <CheckIcon class="h-4 w-4" />
              </div>
              <component
                :is="option.icon"
                v-if="option.icon"
                class="mr-2 h-4 w-4 text-muted-foreground"
              />
              <span>{{ option.label }}</span>
              <span
                v-if="facets && facets[option.value] !== undefined"
                class="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs text-muted-foreground"
              >
                {{ facets[option.value] }}
              </span>
            </CommandItem>
          </CommandGroup>
          <template v-if="selected.size > 0">
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                :value="{ label: t('sessions.filters.clear') }"
                class="justify-center text-center"
                @select="clear"
              >
                {{ t('sessions.filters.clear') }}
              </CommandItem>
            </CommandGroup>
          </template>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
